/**
 * Desktop Command Dashboard — pure builders.
 *
 * Every number here is derived deterministically from rows the app already
 * loads (project tasks, estimates, invoices, finance movements, time logs).
 * Nothing is estimated or invented: when a source is missing the builder
 * returns null / empty and the UI shows an explicit unavailable state.
 */
import { dayIndex, dayIndexToISO, isPlannableProject, weekdayOf } from "../planning/planningModel";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function formatShortDate(iso) {
  const idx = dayIndex(iso);
  if (idx == null) return "";
  const [, m, d] = String(iso).split("-").map(Number);
  return `${MONTHS[m - 1]} ${d}`;
}

function usableTasks(tasks, projects) {
  const plannable = new Map((projects || []).filter(isPlannableProject).map((p) => [p.project_id, p]));
  return (tasks || []).filter(
    (task) => task && task.is_active !== false && plannable.has(task.project_id),
  );
}

/** open → overdue | today | upcoming | unscheduled; completed → completed */
export function classifyTask(task, todayIdx) {
  if (task.status === "completed") return "completed";
  const due = dayIndex(task.due_date);
  if (due == null) return "unscheduled";
  if (due < todayIdx) return "overdue";
  if (due === todayIdx) return "today";
  return "upcoming";
}

export function buildTaskOverview({ tasks = [], projects = [], today }) {
  const todayIdx = dayIndex(today);
  const out = { total: 0, overdue: 0, today: 0, upcoming: 0, unscheduled: 0, completed: 0 };
  if (todayIdx == null) return out;
  usableTasks(tasks, projects).forEach((task) => {
    out[classifyTask(task, todayIdx)] += 1;
    out.total += 1;
  });
  return out;
}

/** Per active project: completion from real task rows. No tasks → no percentage. */
export function buildProjectHealth({ tasks = [], projects = [], today }) {
  const todayIdx = dayIndex(today);
  const byProject = new Map();
  usableTasks(tasks, projects).forEach((task) => {
    const row = byProject.get(task.project_id) || { total: 0, completed: 0, overdue: 0 };
    row.total += 1;
    const kind = todayIdx == null ? "upcoming" : classifyTask(task, todayIdx);
    if (kind === "completed") row.completed += 1;
    if (kind === "overdue") row.overdue += 1;
    byProject.set(task.project_id, row);
  });

  const rows = (projects || []).filter(isPlannableProject).map((project) => {
    const stats = byProject.get(project.project_id) || { total: 0, completed: 0, overdue: 0 };
    const percent = stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : null;
    const health = stats.total === 0 ? "no_tasks" : stats.overdue > 0 ? "overdue" : "on_track";
    return {
      projectId: project.project_id,
      name: project.project_name || "Project",
      total: stats.total,
      completed: stats.completed,
      open: stats.total - stats.completed,
      overdue: stats.overdue,
      percent,
      health,
    };
  });

  const rank = { overdue: 0, on_track: 1, no_tasks: 2 };
  rows.sort(
    (a, b) =>
      rank[a.health] - rank[b.health] ||
      (a.percent ?? 101) - (b.percent ?? 101) ||
      a.name.localeCompare(b.name),
  );

  return {
    rows,
    counts: {
      onTrack: rows.filter((r) => r.health === "on_track").length,
      overdue: rows.filter((r) => r.health === "overdue").length,
      noTasks: rows.filter((r) => r.health === "no_tasks").length,
    },
  };
}

/**
 * Task health for ONE project's tasks — the single definition used by the Dashboard
 * Project Health rows, the Project cards and Project Detail (same classifyTask, same
 * rounding). Inactive tasks are ignored; no tasks → no percentage (never a guess).
 */
export function summarizeProjectTasks(tasks = [], today) {
  const out = { total: 0, completed: 0, overdue: 0, today: 0, upcoming: 0, unscheduled: 0, remaining: 0, percent: null };
  const todayIdx = dayIndex(today);
  if (todayIdx == null) return out;
  (tasks || []).forEach((task) => {
    if (!task || task.is_active === false) return;
    out.total += 1;
    const kind = classifyTask(task, todayIdx);
    if (kind === "completed") out.completed += 1;
    else {
      out.remaining += 1;
      out[kind] += 1;
    }
  });
  out.percent = out.total ? Math.round((out.completed / out.total) * 100) : null;
  return out;
}

/** Open dated tasks per day for today .. today+days-1. */
export function buildPlanningDays({ tasks = [], projects = [], today, days = 7 }) {
  const todayIdx = dayIndex(today);
  if (todayIdx == null) return { days: [], total: 0, max: 0 };
  const counts = new Map();
  usableTasks(tasks, projects).forEach((task) => {
    if (task.status === "completed") return;
    const due = dayIndex(task.due_date);
    if (due == null || due < todayIdx || due >= todayIdx + days) return;
    counts.set(due, (counts.get(due) || 0) + 1);
  });
  const list = [];
  for (let i = 0; i < days; i += 1) {
    const idx = todayIdx + i;
    list.push({
      iso: dayIndexToISO(idx),
      weekday: WEEKDAYS[weekdayOf(idx)],
      dateLabel: formatShortDate(dayIndexToISO(idx)),
      count: counts.get(idx) || 0,
      isToday: i === 0,
    });
  }
  const total = list.reduce((sum, d) => sum + d.count, 0);
  return { days: list, total, max: Math.max(0, ...list.map((d) => d.count)) };
}

/**
 * Project ids that contain at least one overdue task.
 *
 * Overdue is `classifyTask`: a usable task (active, on a plannable project)
 * whose status is not completed and whose due date is before today.
 * Completed, inactive, undated, and due-today tasks do not qualify.
 * Archived and other non-plannable projects do not qualify.
 * A project is included once, however many overdue tasks it has.
 * Project-record age is not part of this predicate.
 */
export function projectIdsWithOverdueTasks({ tasks = [], projects = [], today } = {}) {
  const todayIdx = dayIndex(today);
  if (todayIdx == null) return new Set();
  const ids = new Set();
  usableTasks(tasks, projects).forEach((task) => {
    if (classifyTask(task, todayIdx) === "overdue") ids.add(task.project_id);
  });
  return ids;
}

/** How many plannable projects contain at least one overdue task. */
export function countProjectsWithOverdueTasks(input) {
  return projectIdsWithOverdueTasks(input).size;
}

export const PIPELINE_STAGES = Object.freeze([
  { key: "draft", label: "Draft" },
  { key: "ready", label: "Ready" },
  { key: "sent", label: "Sent" },
  { key: "approved", label: "Approved" },
]);

/** Stage counts and summed total_amount from the estimates the user can already see. */
export function buildEstimatePipeline(rows = [], resolveStatus = (s) => s) {
  const stages = PIPELINE_STAGES.map((stage) => ({ ...stage, count: 0, value: 0 }));
  (rows || []).forEach((row) => {
    const stage = stages.find((s) => s.key === resolveStatus(row?.status));
    if (!stage) return;
    stage.count += 1;
    const amount = Number(row?.total_amount);
    if (Number.isFinite(amount)) stage.value += amount;
  });
  return {
    stages,
    totalCount: stages.reduce((sum, s) => sum + s.count, 0),
    totalValue: stages.reduce((sum, s) => sum + s.value, 0),
  };
}

export const CASH_FLOW_RANGES = Object.freeze({
  "30D": { days: 30, bucket: "day" },
  "90D": { days: 90, bucket: "week" },
  "1Y": { days: 365, bucket: "month" },
});

function movementEventType(movement) {
  const events = movement?.finance_events;
  if (Array.isArray(events)) return events[0]?.event_type;
  return events?.event_type;
}

/**
 * Money in / money out on active asset accounts (transfers and opening
 * balances excluded, same basis as Financial Position's "today" totals),
 * bucketed over the selected range.
 */
export function buildCashFlow({ accounts = [], movements = [], range = "90D", today }) {
  const config = CASH_FLOW_RANGES[range] || CASH_FLOW_RANGES["90D"];
  const todayIdx = dayIndex(today);
  if (todayIdx == null) return { buckets: [], totals: { in: 0, out: 0, net: 0 }, hasActivity: false };
  const assetIds = new Set(
    (accounts || []).filter((a) => a && a.active !== false && a.class === "asset").map((a) => a.id),
  );
  const startIdx = todayIdx - config.days + 1;

  const size = config.bucket === "day" ? 1 : config.bucket === "week" ? 7 : 30;
  const count = Math.ceil(config.days / size);
  const buckets = [];
  for (let i = 0; i < count; i += 1) {
    const from = startIdx + i * size;
    const to = Math.min(from + size - 1, todayIdx);
    buckets.push({ fromIso: dayIndexToISO(from), toIso: dayIndexToISO(to), from, to, in: 0, out: 0 });
  }

  let hasActivity = false;
  (movements || []).forEach((movement) => {
    if (!movement || movement.status !== "posted" || !assetIds.has(movement.account_id)) return;
    const type = movementEventType(movement);
    if (type === "transfer" || type === "opening_balance_seed") return;
    const idx = dayIndex(movement.occurred_on);
    if (idx == null || idx < startIdx || idx > todayIdx) return;
    const amount = Number(movement.amount);
    if (!Number.isFinite(amount) || amount <= 0) return;
    const bucket = buckets[Math.min(buckets.length - 1, Math.floor((idx - startIdx) / size))];
    if (movement.flow === "inbound") bucket.in += amount;
    else if (movement.flow === "outbound") bucket.out += amount;
    else return;
    hasActivity = true;
  });

  buckets.forEach((b) => {
    b.net = b.in - b.out;
    b.label = b.from === b.to ? formatShortDate(b.fromIso) : `${formatShortDate(b.fromIso)} – ${formatShortDate(b.toIso)}`;
  });
  const totals = buckets.reduce(
    (acc, b) => ({ in: acc.in + b.in, out: acc.out + b.out, net: acc.net + b.net }),
    { in: 0, out: 0, net: 0 },
  );
  return { buckets, totals, hasActivity };
}

/** Actual hours per day for the 7 days starting at `weekStartIso` (+ offsetWeeks*7). */
export function buildLabourWeek({ logs = [], weekStartIso, offsetWeeks = 0 }) {
  const startIdx = dayIndex(weekStartIso);
  if (startIdx == null) return { days: [], total: 0, max: 0 };
  const first = startIdx + offsetWeeks * 7;
  const hours = new Map();
  (logs || []).forEach((log) => {
    const idx = dayIndex(log?.date_iso);
    if (idx == null || idx < first || idx > first + 6) return;
    const value = Number(log.total_hours);
    if (Number.isFinite(value)) hours.set(idx, (hours.get(idx) || 0) + value);
  });
  const days = [];
  for (let i = 0; i < 7; i += 1) {
    const idx = first + i;
    days.push({
      iso: dayIndexToISO(idx),
      weekday: WEEKDAYS[weekdayOf(idx)],
      hours: Math.round((hours.get(idx) || 0) * 10) / 10,
    });
  }
  const total = Math.round(days.reduce((s, d) => s + d.hours, 0) * 10) / 10;
  return { days, total, max: Math.max(0, ...days.map((d) => d.hours)) };
}

export function formatMoneyCompact(value) {
  const n = Number(value);
  const safe = Number.isFinite(n) ? n : 0;
  try {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
      maximumFractionDigits: Math.abs(safe) >= 1000 ? 0 : 2,
    }).format(safe);
  } catch {
    return `$${safe.toFixed(0)}`;
  }
}

/** Per-project task counts (+ portfolio totals) from real task rows, for the Projects landing. */
export function buildProjectTaskStats({ tasks = [], projects = [], today, horizonDays = 7 }) {
  const todayIdx = dayIndex(today);
  const byProject = new Map();
  const totals = { overdue: 0, dueThisWeek: 0 };
  (projects || []).filter(isPlannableProject).forEach((project) => {
    byProject.set(project.project_id, {
      total: 0,
      completed: 0,
      overdue: 0,
      today: 0,
      upcoming: 0,
      assignees: new Set(),
    });
  });
  if (todayIdx == null) return { byProject, totals };
  (tasks || []).forEach((task) => {
    if (!task || task.is_active === false) return;
    const row = byProject.get(task.project_id);
    if (!row) return;
    row.total += 1;
    if (task.assignee_id) row.assignees.add(task.assignee_id);
    const kind = classifyTask(task, todayIdx);
    if (kind === "completed") row.completed += 1;
    else if (kind === "overdue") {
      row.overdue += 1;
      totals.overdue += 1;
    } else if (kind === "today") {
      row.today += 1;
      totals.dueThisWeek += 1;
    } else if (kind === "upcoming") {
      row.upcoming += 1;
      if (dayIndex(task.due_date) < todayIdx + horizonDays) totals.dueThisWeek += 1;
    }
  });
  byProject.forEach((row) => {
    row.assigneeCount = row.assignees.size;
    delete row.assignees;
  });
  return { byProject, totals };
}
